import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, X } from "lucide-react";
import {
  CURRENT_VERSION,
  LAST_VERSION_KEY,
  RELEASE_FEATURE_BRIEF,
  TOUR_STORAGE_KEY,
} from "./tour-content";

const AUTO_DISMISS_MS = 3000;
const CLOSE_ANIMATION_MS = 180;

export function WhatsNewButton() {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [closing, setClosing] = useState(false);
  const [autoDismissPaused, setAutoDismissPaused] = useState(false);
  const autoDismissTimerRef = useRef<number | null>(null);
  const closeTimerRef = useRef<number | null>(null);

  const clearAutoDismissTimer = useCallback(() => {
    if (autoDismissTimerRef.current === null) return;
    window.clearTimeout(autoDismissTimerRef.current);
    autoDismissTimerRef.current = null;
  }, []);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current === null) return;
    window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
  }, []);

  const finishClose = useCallback(() => {
    setVisible(false);
    setExpanded(false);
    setClosing(false);
    closeTimerRef.current = null;
  }, []);

  const beginClose = useCallback(
    (markVersionSeen: boolean) => {
      if (markVersionSeen) {
        localStorage.setItem(LAST_VERSION_KEY, CURRENT_VERSION);
      }

      clearAutoDismissTimer();
      clearCloseTimer();
      setClosing(true);
      closeTimerRef.current = window.setTimeout(
        finishClose,
        CLOSE_ANIMATION_MS,
      );
    },
    [clearAutoDismissTimer, clearCloseTimer, finishClose],
  );

  useEffect(() => {
    const lastVersion = localStorage.getItem(LAST_VERSION_KEY);
    const tourDone = localStorage.getItem(TOUR_STORAGE_KEY);

    if (tourDone && lastVersion && lastVersion !== CURRENT_VERSION) {
      setExpanded(false);
      setClosing(false);
      setVisible(true);
      return;
    }

    if (tourDone && !lastVersion) {
      localStorage.setItem(LAST_VERSION_KEY, CURRENT_VERSION);
    }
  }, []);

  useEffect(() => {
    if (!visible || expanded || closing || autoDismissPaused) return;

    autoDismissTimerRef.current = window.setTimeout(
      () => beginClose(false),
      AUTO_DISMISS_MS,
    );

    return clearAutoDismissTimer;
  }, [
    autoDismissPaused,
    beginClose,
    clearAutoDismissTimer,
    closing,
    expanded,
    visible,
  ]);

  useEffect(() => {
    return () => {
      clearAutoDismissTimer();
      clearCloseTimer();
    };
  }, [clearAutoDismissTimer, clearCloseTimer]);

  if (!visible) return null;

  const closeForSession = () => {
    beginClose(false);
  };

  const dismissCurrentVersion = () => {
    beginClose(true);
  };

  const expandBrief = () => {
    if (closing || expanded) return;
    clearAutoDismissTimer();
    setExpanded(true);
  };

  return (
    <section
      role="dialog"
      aria-label={`Fintheon Epoch ${CURRENT_VERSION} changelog`}
      data-expanded={expanded ? "true" : "false"}
      data-position="bottom-left"
      onMouseEnter={() => setAutoDismissPaused(true)}
      onMouseLeave={() => setAutoDismissPaused(false)}
      onFocus={() => setAutoDismissPaused(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setAutoDismissPaused(false);
        }
      }}
      className={`fintheon-toast-surface group t-modal t-resize fixed bottom-14 left-14 z-[100] origin-bottom-left overflow-hidden rounded-lg text-[var(--fintheon-text)] max-[767px]:bottom-[calc(env(safe-area-inset-bottom,0px)+1rem)] max-[767px]:left-3 ${
        expanded
          ? "max-h-[calc(100vh-10rem)] w-[min(22rem,calc(100vw-1.5rem))]"
          : "w-[min(17.5rem,calc(100vw-1.5rem))]"
      } ${closing ? "is-closing" : "is-open"}`}
    >
      <div className={expanded ? "px-3 py-3" : "px-3 py-2"}>
        <div className="flex items-start gap-1">
          <button
            type="button"
            aria-expanded={expanded}
            onClick={expandBrief}
            className="group min-w-0 flex-1 border-0 bg-transparent p-0 text-left"
          >
            <p className="truncate text-[11px] font-semibold leading-none tracking-[0.01em] text-[var(--fintheon-text)]/72">
              {RELEASE_FEATURE_BRIEF.title}
            </p>
            <span className="fintheon-toast-hover-actions mt-1 inline-flex items-center gap-1 text-[10px] font-medium text-[var(--fintheon-accent)]/72">
              What changed?
              {!expanded ? (
                <ChevronDown
                  aria-hidden="true"
                  size={11}
                  strokeWidth={1.6}
                  className="transition-transform duration-200 group-hover:translate-y-0.5"
                />
              ) : null}
            </span>
          </button>
          <button
            type="button"
            onClick={closeForSession}
            aria-label="Close feature update"
            className="fintheon-toast-hover-actions fintheon-toast-icon-button grid h-5 w-5 shrink-0 place-items-center rounded border-0 bg-transparent text-[var(--fintheon-text)]/46"
          >
            <X size={12} strokeWidth={1.7} />
          </button>
        </div>

        {expanded ? (
          <div
            className="t-panel-slide mt-3 border-t border-[var(--fintheon-accent)]/10 pt-3"
            data-open="true"
          >
            <p className="text-xs leading-relaxed text-[var(--fintheon-text)]/62">
              {RELEASE_FEATURE_BRIEF.intro}
            </p>
            <div className="mt-3 max-h-[38vh] space-y-3 overflow-y-auto pr-1">
              {RELEASE_FEATURE_BRIEF.items.map((item) => (
                <article key={item.title}>
                  <p className="font-mono text-[9px] uppercase tracking-[0.13em] text-[var(--fintheon-accent)]/62">
                    {item.category}
                  </p>
                  <h3 className="mt-1 text-xs font-semibold leading-snug text-[var(--fintheon-text)]/86">
                    {item.title}
                  </h3>
                  <p className="mt-1 text-xs leading-relaxed text-[var(--fintheon-text)]/56">
                    {item.body}
                  </p>
                  <p className="mt-1 text-[11px] leading-relaxed text-[var(--fintheon-accent)]/72">
                    {item.action}
                  </p>
                </article>
              ))}
            </div>
            <div className="mt-3 flex justify-end">
              <button
                type="button"
                onClick={dismissCurrentVersion}
                className="fintheon-toast-hover-actions border-0 bg-transparent p-0 text-[11px] font-medium leading-none text-[var(--fintheon-text)]/46"
              >
                Do not show again
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}
